import { __awaiter, __decorate, __metadata } from "tslib";
import ReactDOM from 'react-dom';
import { Graph as X6Graph, Dom } from '@antv/x6';
import { DisposableCollection, Disposable } from '../../common/disposable';
import { Deferred } from '../../common/deferred';
import { IGraphOptionProvider } from './config';
import { singleton, inject, Syringe } from 'mana-syringe';
import { IGraphCommandService } from '../../command';
import { IModelService } from '../../model-service/interface';
import { IHookService } from '../../hooks';
import { getEdgeReactComponent } from '../../command-contributions/components/context';
import { XFLOW_DEFAULT_EDGE } from '../../constants';
/** !!! 所有需要使用Graph相关信息的地方, 都统一使用IGraphProvider来获取 */
export const IGraphProvider = Symbol('IGraphProvider');
export const ICommandServiceProvider = Symbol('ICommandServiceProvider');
export const IModelServiceProvider = Symbol('IModelServiceProvider');
let GraphManager = class GraphManager {
    constructor() {
        /** 处理画布实例的销毁 */
        this.toDisposce = new DisposableCollection();
        /** 储存画布实例 */
        this.graphMap = new Map();
        /** 获取X6 Graph 实例 */
        this.getGraph = (graphId) => __awaiter(this, void 0, void 0, function* () {
            let graphDefer = this.graphMap.get(graphId);
            if (!graphDefer) {
                graphDefer = new Deferred();
                this.graphMap.set(graphId, graphDefer);
                const options = yield this.optionProvider.getOptions();
                /** 获取hooks实例： hooks方便其他组件可以修改注册graphOpions/events */
                const hooks = this.hookService.hookProvider();
                /** 执行hooks：获取graphOptions */
                const mergedOptions = yield hooks.graphOptions.call(options.x6Options);
                const { graphContainer, edgeRender, nodeRender, edgeTypeParser } = options;
                /** 执行hooks：获取edge label render */
                const edgeRenderMap = yield hooks.reactEdgeLabelRender.call(edgeRender);
                /** 执行hooks：更新nodeRender */
                yield hooks.reactNodeRender.call(nodeRender);
                const { clientHeight, clientWidth } = graphContainer;
                const commandService = yield this.commandServiceProvider.getCommandService();
                const modelService = yield this.modelServiceProvider.getModelService();
                /** 实例化 X6 Graph */
                const graph = new X6Graph(Object.assign(Object.assign({ container: graphContainer, width: clientWidth, height: clientHeight }, mergedOptions), { 
                    /** X6提供了边渲染的钩子, 可以在这里设置边上需要渲染的React内容 */
                    onEdgeLabelRendered: args => {
                        const { edge, container } = args;
                        this.renderEdgeReactLabel({
                            edge,
                            container,
                            edgeRenderMap,
                            edgeTypeParser,
                            modelService,
                            commandService,
                        });
                    } }));
                /** 执行hooks：绑定事件执行api */
                yield hooks.afterGraphInit.call({
                    graph,
                    commandService,
                    modelService,
                    options,
                });
                graphDefer.resolve(graph);
                graph.on('node:moved', ({ node }) => {
                    const nodeData = node.getData();
                    const position = node.position();
                    node.setData(Object.assign(Object.assign({}, nodeData), { x: position === null || position === void 0 ? void 0 : position.x, y: position === null || position === void 0 ? void 0 : position.y }));
                });
                graph.on('node:resized', ({ node }) => {
                    const nodeData = node.getData();
                    const size = node.size();
                    node.setData(Object.assign(Object.assign({}, nodeData), { width: size === null || size === void 0 ? void 0 : size.width, height: size === null || size === void 0 ? void 0 : size.height }));
                });
                this.toDisposce.push(Disposable.create(() => __awaiter(this, void 0, void 0, function* () {
                    yield hooks.beforeGraphDestroy.call({
                        graph,
                        commandService,
                        modelService,
                        options,
                    });
                    this.graphMap.delete(graphId);
                    graph.dispose();
                })));
            }
            return graphDefer.promise;
        });
        this.renderEdgeReactLabel = (args) => __awaiter(this, void 0, void 0, function* () {
            const { container, edgeTypeParser, edge, edgeRenderMap, commandService, modelService } = args;
            const renderKey = edgeTypeParser(edge === null || edge === void 0 ? void 0 : edge.data) || XFLOW_DEFAULT_EDGE;
            const reactComponent = edgeRenderMap.get(renderKey);
            if (!reactComponent) {
                return;
            }
            if (edge && (edge === null || edge === void 0 ? void 0 : edge.data) && reactComponent) {
                const content = this.edgeAppendForeignObject(edge, container);
                const WrappedReactComponent = getEdgeReactComponent(reactComponent, commandService, modelService)(edge);
                ReactDOM.render(WrappedReactComponent, content);
            }
        });
    }
    /** 实现在连线上渲染React节点 */
    edgeAppendForeignObject(x6Edge, container) {
        const fo = Dom.createSvgElement('foreignObject');
        const body = Dom.createElementNS('body', Dom.ns.xhtml);
        const content = Dom.createElementNS('div', Dom.ns.xhtml);
        const edgeData = x6Edge === null || x6Edge === void 0 ? void 0 : x6Edge.data;
        const foWdith = (edgeData === null || edgeData === void 0 ? void 0 : edgeData.edgeContentWidth) || 100;
        const foHeight = (edgeData === null || edgeData === void 0 ? void 0 : edgeData.edgeContentHeigt) || 30;
        fo.setAttribute('width', `${foWdith}`);
        fo.setAttribute('height', `${foHeight}`);
        fo.setAttribute('x', `${(-1.0 * foWdith) / 2}`);
        fo.setAttribute('y', `${(-1.0 * foHeight) / 2}`);
        body.setAttribute('xhtmls', Dom.ns.xhtml);
        body.style.width = '100%';
        body.style.height = '100%';
        body.style.padding = '0';
        body.style.margin = '0';
        body.style.background = 'unset';
        body.style.overflow = 'visible';
        body.className = 'xflow-edge-label-body';
        content.style.width = '100%';
        content.style.height = '100%';
        body.appendChild(content);
        fo.appendChild(body);
        container.appendChild(fo);
        return content;
    }
    dispose() {
        this.toDisposce.dispose();
    }
};
__decorate([
    inject(IGraphOptionProvider),
    __metadata("design:type", Object)
], GraphManager.prototype, "optionProvider", void 0);
__decorate([
    inject(IHookService),
    __metadata("design:type", Object)
], GraphManager.prototype, "hookService", void 0);
__decorate([
    inject(ICommandServiceProvider),
    __metadata("design:type", Object)
], GraphManager.prototype, "commandServiceProvider", void 0);
__decorate([
    inject(IModelServiceProvider),
    __metadata("design:type", Object)
], GraphManager.prototype, "modelServiceProvider", void 0);
GraphManager = __decorate([
    singleton()
], GraphManager);
export { GraphManager };
export const registerGraphModule = (register) => {
    /** 注册GraphManager */
    register(GraphManager);
    register(IGraphProvider, {
        lifecycle: Syringe.Lifecycle.singleton,
        useDynamic: context => {
            return {
                getGraphInstance: () => __awaiter(void 0, void 0, void 0, function* () {
                    const graphManager = context.container.get(GraphManager);
                    const graphOptionProvider = context.container.get(IGraphOptionProvider);
                    const graphConfig = yield (graphOptionProvider === null || graphOptionProvider === void 0 ? void 0 : graphOptionProvider.getOptions());
                    const x6Graph = yield (graphManager === null || graphManager === void 0 ? void 0 : graphManager.getGraph(graphConfig.graphId));
                    return x6Graph;
                }),
                getGraphOptions: () => __awaiter(void 0, void 0, void 0, function* () {
                    const graphOptionProvider = context.container.get(IGraphOptionProvider);
                    return graphOptionProvider === null || graphOptionProvider === void 0 ? void 0 : graphOptionProvider.getOptions();
                }),
            };
        },
    });
    register(ICommandServiceProvider, {
        lifecycle: Syringe.Lifecycle.singleton,
        useDynamic: context => {
            return {
                getCommandService: () => __awaiter(void 0, void 0, void 0, function* () {
                    const commandService = context.container.get(IGraphCommandService);
                    return commandService;
                }),
            };
        },
    });
    register(IModelServiceProvider, {
        lifecycle: Syringe.Lifecycle.singleton,
        useDynamic: context => {
            return {
                getModelService: () => __awaiter(void 0, void 0, void 0, function* () {
                    const modelService = context.container.get(IModelService);
                    return modelService;
                }),
            };
        },
    });
};
//# sourceMappingURL=graph-provider.js.map